import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, RefreshCw, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { emailConfirmationService } from '../lib/supabase';
import toast from 'react-hot-toast';

const EmailVerificationBanner: React.FC = () => {
  const { user, isEmailVerified } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [isResending, setIsResending] = useState(false);

  // Don't show banner if email is verified or user is not logged in
  if (!user || isEmailVerified || !isVisible) {
    return null;
  }

  const handleResendConfirmation = async () => {
    if (!user?.email) {
      toast.error('No email address found.');
      return;
    }

    setIsResending(true);
    try {
      await emailConfirmationService.resendConfirmationEmail(user.email);
      toast.success('Confirmation email sent! Please check your inbox.');
    } catch (error: unknown) {
      console.error('Error resending confirmation:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('rate limit')) {
        toast.error('Please wait before requesting another confirmation email.');
      } else {
        toast.error('Failed to resend confirmation email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className="bg-amber-50 border-b border-amber-200 px-4 py-3"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Mail className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm text-amber-800">
                <strong>Please verify your email address.</strong> We've sent a confirmation link to{' '}
                <span className="font-medium">{user.email}</span>. 
                You must verify your email before accessing protected areas of the platform. 
                Check your inbox and spam folder.
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleResendConfirmation}
              disabled={isResending}
              className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Resend</span>
                </>
              )}
            </button>
            
            <button
              onClick={handleDismiss}
              className="text-amber-600 hover:text-amber-800 p-1"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailVerificationBanner;



